import { type JSX, type ReactNode } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import type { Callback } from 'ts/base/Callback';
import type { AddToTaskFilterValues } from 'ts/commons/filters/findings/AddedToTaskFilter';
import type { AssessmentFilterValues } from 'ts/commons/filters/findings/AssessmentFilter';
import type { BlacklistRationalFilterValues } from 'ts/commons/filters/findings/BlacklistRationalFilter';
import type { CategoryGroupTypeFilterValues } from 'ts/commons/filters/findings/CategoryGroupTypeFilter';
import { FindingsFilter } from 'ts/commons/filters/findings/FindingsFilter';
import type { FlaggingOptionFilterValues } from 'ts/commons/filters/findings/FlaggingOptionFilter';
import type { GuidelineFilterValues } from 'ts/commons/filters/findings/GuidelineFilter';
import type { IssueQueryFilterValues } from 'ts/commons/filters/findings/IssueQueryFilter';
import type { PathsFilterValues } from 'ts/commons/filters/findings/PathsFilter';
import type { RegexFilterValues } from 'ts/commons/filters/RegexFilter';
import { Form } from 'ts/components/Form';

/** The "public" API of the filter options. */
export type FindingFilterValues = AddToTaskFilterValues &
	AssessmentFilterValues &
	BlacklistRationalFilterValues &
	CategoryGroupTypeFilterValues &
	PathsFilterValues &
	RegexFilterValues &
	GuidelineFilterValues &
	IssueQueryFilterValues &
	FlaggingOptionFilterValues;

/** Props for FindingsFilterForm. */
export type FindingsFilterFormProps = {
	findingsFilter: FindingsFilter;
	onSubmit: Callback<FindingsFilter>;
	children: ReactNode;
};

/** Context provider for the issue query. */
export function FindingsFilterForm({ findingsFilter, onSubmit, children }: FindingsFilterFormProps): JSX.Element {
	const methods = useForm<FindingFilterValues>({ defaultValues: findingsFilter.toFindingFilterValues() });
	const { handleSubmit } = methods;
	const apply: SubmitHandler<FindingFilterValues> = (findingFilter, event) => {
		event?.preventDefault();
		onSubmit(FindingsFilter.fromFindingFilterValues(findingFilter));
	};

	return (
		<FormProvider {...methods}>
			<Form id="findings-filter-form" onSubmit={handleSubmit(apply)}>
				{children}
			</Form>
		</FormProvider>
	);
}
